import {
  Button,
  ButtonProps,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Stack,
  useColorModeValue,
  useDisclosure,
  useOutsideClick,
} from "@chakra-ui/react";
import { IconProps } from "@cloudscape-design/components";

import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { MdFiberSmartRecord as SessionActiveIcon } from "react-icons/md";
import { useMatch } from "react-router-dom";
import { selectSessionState } from "../features/sessionSlice";
import { useAppSelector } from "../hooks";
import { SESSION_STATE } from "../session/session-state";
import type { SessionData } from "../session/types";
import { routes } from "../utils/routes";
import { ActiveSessionOverview } from "./ActiveSessionOverview";
import { AppBarButtonPopoverContent } from "./AppBarButtonPopover";
import { ExpectedWaitTime } from "./ExpectedWaitTime";
import { MotionIcon } from "./MotionIcon";
import { ProgressDisplay } from "./ProgressDisplay";
import { SessionInformation } from "./SessionInformation";

function ActiveSessionButtonIcon({
  sessionState,
  isSessionActive,
  ...props
}: {
  sessionState: SessionData["state"];
  isSessionActive: boolean;
} & IconProps) {
  if (
    sessionState <= SESSION_STATE.READY ||
    sessionState === SESSION_STATE.ENDING
  ) {
    return <Spinner color="brand.500" size="sm" />;
  }

  if (isSessionActive) {
    return (
      <MotionIcon
        initial={{ opacity: 1 }}
        animate={{ opacity: 0.2 }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          type: "spring",
          duration: 1,
        }}
        color="red.400"
        as={SessionActiveIcon}
        size={props.size}
      />
    );
  }

  return null;
}

function ActiveSessionButtonLabel({
  isSessionReady,
  isSessionEnding,
  isSessionActive,
  isDesktopSession,
  progress,
}: {
  isSessionReady: boolean;
  isSessionEnding: boolean;
  isSessionActive: boolean;
  isDesktopSession: boolean;
  progress?: number;
}) {
  const { t } = useTranslation();

  if (isSessionReady) {
    return t("session.ready_short");
  }

  if (isSessionEnding) {
    return t("session.ending");
  }

  if (isSessionActive) {
    return t("session.active");
  }

  if (isDesktopSession) {
    return <ProgressDisplay progress={progress} />;
  }

  return <ExpectedWaitTime />;
}

export function ActiveSessionButton(props: ButtonProps) {
  const { t } = useTranslation();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const popoverRef = useRef(null);
  useOutsideClick({
    ref: popoverRef,
    handler: onClose,
  });
  const currentSession = useAppSelector(selectSessionState);
  const borderColor = useColorModeValue("gray.600", "white");
  const isOnSessionPage = !!useMatch(routes.session);

  return (
    <Popover
      trigger="click"
      placement="bottom-end"
      isOpen={isOpen}
      closeOnBlur={true}
      returnFocusOnClose={false}
    >
      <PopoverTrigger>
        <Button
          leftIcon={
            <ActiveSessionButtonIcon
              isSessionActive={currentSession.isActive}
              sessionState={currentSession.state}
            />
          }
          variant="outline"
          borderColor={borderColor}
          onClick={onToggle}
          {...props}
        >
          <ActiveSessionButtonLabel
            isSessionReady={currentSession.isReady}
            isSessionEnding={currentSession.isEnding}
            isSessionActive={currentSession.isActive}
            isDesktopSession={currentSession.isDesktop}
            progress={currentSession.progress}
          />
        </Button>
      </PopoverTrigger>
      <AppBarButtonPopoverContent width="xs" ref={popoverRef}>
        <PopoverHeader>{t("session.debug.header")}</PopoverHeader>
        <PopoverArrow />
        <PopoverCloseButton onClick={onClose} top={2} />
        <PopoverBody px={0}>
          <Stack>
            {!isOnSessionPage ? (
              <ActiveSessionOverview
                onGotoSession={onClose}
                session={currentSession}
              />
            ) : (
              <SessionInformation />
            )}
          </Stack>
        </PopoverBody>
      </AppBarButtonPopoverContent>
    </Popover>
  );
}
